/**
 * Team roster table for Settings > My-Kitchen (Plan 19).
 *
 * Lists active CHEF_ADMIN / CHEF_STAFF Memberships at the kitchen org +
 * any pending Invitations. CHEF_ADMIN can:
 *   - Invite a new admin or staff member (opens InviteStaffSheet)
 *   - Remove a Membership (soft-archive). Server-side guards block
 *     last-admin removal and self-lockout.
 *
 * Visible only to CHEF_ADMIN per the plan's Acceptance Criterion 9 —
 * the page-level gate omits this section for CHEF_STAFF.
 */

import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@ordo/ui/dropdown-menu'
import { EmptyState } from '@ordo/ui/empty-state'
import { Skeleton } from '@ordo/ui/skeleton'
import { toast } from '@ordo/ui/sonner'
import { MoreHorizontal, UserPlus, Users } from 'lucide-react'
import { useState } from 'react'

import {
  type ChefRole,
  chefRoleLabel,
  type KitchenPendingInvite,
  type KitchenTeamMembership,
  useKitchenTeamQuery,
  useRemoveChefMembershipMutation,
} from '@/api/kitchen-settings'

import { InviteStaffSheet } from './invite-staff-sheet'

export interface TeamRosterTableProps {
  supplier?: boolean
  kitchenOrgId: string
  /** Central User.id of the signed-in actor; used to surface "(you)" + disable self-remove when appropriate. */
  actorUserId: string
}

export function TeamRosterTable({ kitchenOrgId, actorUserId, supplier = false }: TeamRosterTableProps) {
  const teamQuery = useKitchenTeamQuery(kitchenOrgId)
  const removeMutation = useRemoveChefMembershipMutation(kitchenOrgId)
  const [inviteOpen, setInviteOpen] = useState(false)

  async function handleRemove(membershipId: string) {
    try {
      await removeMutation.mutateAsync(membershipId)
      toast.success('Team member removed')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Try again in a moment.'
      toast.error("Couldn't remove team member", { description: message })
    }
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-2 pb-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4" />
              Team
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Admins manage the kitchen; staff handle production tasks. Phone is required so we can SMS shipment events.
            </p>
          </div>
          <Button type="button" size="sm" onClick={() => setInviteOpen(true)}>
            <UserPlus className="mr-1 h-4 w-4" />
            Invite staff
          </Button>
        </CardHeader>
        <CardContent>
          {teamQuery.isPending && !teamQuery.data ? (
            <RosterSkeleton />
          ) : teamQuery.error ? (
            <RosterError message={teamQuery.error.message} />
          ) : teamQuery.data ? (
            <RosterBody
              memberships={teamQuery.data.memberships}
              invitations={teamQuery.data.pendingInvitations}
              actorUserId={actorUserId}
              onRemove={handleRemove}
              removePending={removeMutation.isPending}
            />
          ) : null}
        </CardContent>
      </Card>

      <InviteStaffSheet
        supplier={supplier}
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        kitchenOrgId={kitchenOrgId}
      />
    </>
  )
}

function RosterBody({
  memberships,
  invitations,
  actorUserId,
  onRemove,
  removePending,
}: {
  memberships: KitchenTeamMembership[]
  invitations: KitchenPendingInvite[]
  actorUserId: string
  onRemove: (id: string) => void
  removePending: boolean
}) {
  if (memberships.length === 0 && invitations.length === 0) {
    return (
      <EmptyState
        icon={<Users className="h-10 w-10" />}
        title="No team members yet"
        description="Invite admins and staff to give them access to this kitchen."
        padding="sm"
      />
    )
  }
  return (
    <div className="flex flex-col gap-1">
      {memberships.map((m) => (
        <MembershipRow
          key={m.id}
          membership={m}
          actorUserId={actorUserId}
          onRemove={onRemove}
          removePending={removePending}
        />
      ))}
      {invitations.map((i) => (
        <InvitationRow key={i.id} invitation={i} />
      ))}
    </div>
  )
}

function MembershipRow({
  membership,
  actorUserId,
  onRemove,
  removePending,
}: {
  membership: KitchenTeamMembership
  actorUserId: string
  onRemove: (id: string) => void
  removePending: boolean
}) {
  const isYou = membership.userId === actorUserId
  const fullName = [membership.user.firstName, membership.user.lastName].filter(Boolean).join(' ').trim() || '(no name)'
  return (
    <div className="flex items-center gap-3 border-b py-3 last:border-b-0">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{fullName}</span>
          {isYou ? (
            <Badge variant="secondary" className="text-xs">
              You
            </Badge>
          ) : null}
        </div>
        <span className="truncate text-xs text-muted-foreground">
          {membership.user.email ?? 'No email on file'} · {membership.user.phoneNumber ?? 'No phone on file'}
        </span>
      </div>
      <Badge variant={membership.profileCode === 'CHEF_ADMIN' ? 'default' : 'outline'} className="shrink-0">
        {chefRoleLabel(membership.profileCode)}
      </Badge>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Member actions" disabled={removePending}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => onRemove(membership.id)} className="text-destructive">
            Remove from team
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

function InvitationRow({ invitation }: { invitation: KitchenPendingInvite }) {
  return (
    <div className="flex items-center gap-3 border-b py-3 last:border-b-0">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-sm font-medium">{invitation.email}</span>
        <span className="text-xs text-muted-foreground">
          Invited — magic link expires {new Date(invitation.expiresAt).toLocaleDateString()}
        </span>
      </div>
      <Badge variant="outline" className="shrink-0 text-xs">
        Pending {chefRoleLabel(invitation.profileCode as ChefRole)}
      </Badge>
    </div>
  )
}

function RosterSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
    </div>
  )
}

function RosterError({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
      Couldn't load the team. {message}
    </div>
  )
}
